/**
 * Client registers: four hand-maintained spreadsheets (vaccinations, routine
 * health screening, client plans, restrictive practices) for the cohort of
 * disability and aged care clients in supported living. Every percentage is
 * a RegisterPercentage object whose value is a 0..1 FRACTION.
 *
 * A couple of rows per register carry a mistyped client code, so they stay
 * unmatched and show up in the review queue — the same people every time.
 */
import { addDays, currentQuarter, now, quarterOf, ymd } from '../../clock';
import { Rng, rng } from '../../rng';
import { Client, activeClients } from '../../world';
import { DAY, round4, today } from './common';

export type RegisterKey = 'routine_tests' | 'vaccination' | 'plans' | 'restrictive_practices';
export const REGISTER_KEYS: RegisterKey[] = ['routine_tests', 'vaccination', 'plans', 'restrictive_practices'];

const FILES: Record<RegisterKey, string> = {
    routine_tests: 'Routine Test Register 2026.xlsx',
    vaccination: 'Client Vaccination Register.xlsx',
    plans: 'Client Plans and Review Dates.xlsx',
    restrictive_practices: 'Restrictive Practice Register.xlsx',
};

const SHEETS: Record<RegisterKey, string> = {
    routine_tests: 'Tests',
    vaccination: 'Vaccinations',
    plans: 'Plans',
    restrictive_practices: 'Register',
};

const TABLES: Record<RegisterKey, string> = {
    routine_tests: 'routine_test',
    vaccination: 'client_vaccination',
    plans: 'client_plan',
    restrictive_practices: 'restrictive_practice',
};

const IMPORT_MINUTE: Record<RegisterKey, number> = { routine_tests: 30, vaccination: 31, plans: 31, restrictive_practices: 32 };

/* ------------------------------------------------------------------ */
/* Cohort and identities                                               */
/* ------------------------------------------------------------------ */

export interface Member {
    client: Client;
    gender: 'Female' | 'Male';
    age: number;
    diabetic: boolean;
}

/** The register cohort: 24 NDIS and aged care clients in supported living. */
export function registerCohort(): Member[] {
    const pool = activeClients().filter(c => c.program === 'Disability Services (NDIS)' || c.program === 'Aged Care');
    return rng('registers:cohort')
        .sample(pool, 24)
        .sort((a, b) => a.id - b.id)
        .map(client => {
            const r = rng(`registers:member:${client.code}`);
            const aged = client.program === 'Aged Care';
            return {
                client,
                gender: r.bool(0.58) ? 'Female' : 'Male',
                age: aged ? r.int(58, 86) : r.int(22, 61),
                diabetic: r.bool(aged ? 0.5 : 0.3),
            };
        });
}

/** Rows typed with a code the importer cannot match, per register. */
function typos(register: RegisterKey): Map<string, { raw: string; status: 'unmatched' | 'ambiguous' }> {
    const cohort = registerCohort();
    const pick = (i: number) => cohort[i % cohort.length].client.code;
    const out = new Map<string, { raw: string; status: 'unmatched' | 'ambiguous' }>();
    const letterO = (code: string) => code.replace(/0/, 'O');
    const swap = (code: string) => code.slice(0, 1) + code.slice(2, 3) + code.slice(1, 2) + code.slice(3);
    const short = (code: string) => code.slice(0, -1);
    if (register === 'routine_tests') {
        out.set(pick(3), { raw: letterO(pick(3)), status: 'unmatched' });
        out.set(pick(17), { raw: short(pick(17)), status: 'ambiguous' });
    } else if (register === 'vaccination') {
        out.set(pick(9), { raw: swap(pick(9)), status: 'unmatched' });
    } else if (register === 'plans') {
        out.set(pick(14), { raw: letterO(pick(14)), status: 'unmatched' });
    }
    return out;
}

interface Identity {
    client: string | null;
    client_matched: boolean;
    raw_name?: string;
}

function identity(register: RegisterKey, m: Member): Identity {
    const t = typos(register).get(m.client.code);
    return t ? { client: null, client_matched: false, raw_name: t.raw } : { client: m.client.code, client_matched: true };
}

function matchedCount(register: RegisterKey, members: Member[]): number {
    const t = typos(register);
    return members.filter(m => !t.has(m.client.code)).length;
}

/* ------------------------------------------------------------------ */
/* Shared shapes                                                       */
/* ------------------------------------------------------------------ */

type Basis = 'register_roster' | 'register_roster_eligible' | 'matched_clients';

function pct(numerator: number, denominator: number, basis: Basis, label: string, asAt: string): Record<string, unknown> {
    return {
        value: denominator ? round4(numerator / denominator) : null,
        numerator,
        denominator,
        denominator_basis: basis,
        denominator_label: label,
        cohort: 'sil_register',
        as_at: asAt,
    };
}

function fileLabel(register: RegisterKey, scope?: string): string {
    const base = `Supported-living clients named in the ${FILES[register].replace(/\.xlsx$/, '')}`;
    return scope ? `${base} (${scope})` : base;
}

function roster(register: RegisterKey, clients: number): Record<string, unknown> {
    return { clients, denominator_basis: 'register_roster', denominator_label: fileLabel(register), cohort: 'sil_register' };
}

/** Registers are re-imported each morning at about 7:30. */
function lastImported(register: RegisterKey): string {
    const n = now();
    let d = new Date(n.getFullYear(), n.getMonth(), n.getDate(), 7, IMPORT_MINUTE[register], 12);
    if (d > n) {
        d = addDays(d, -1);
    }
    const p = (v: number) => String(v).padStart(2, '0');
    return `${ymd(d)} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

function source(register: RegisterKey, rows: number, needsReview: number): Record<string, unknown> {
    return {
        register,
        expected_file: FILES[register],
        source_file: FILES[register],
        imported: true,
        file_name_differs: false,
        rows,
        needs_review: needsReview,
        last_imported_at: lastImported(register),
    };
}

function matchQuality(register: RegisterKey, members: Member[], asAt: string): Record<string, unknown> {
    return pct(matchedCount(register, members), members.length, 'matched_clients', `${fileLabel(register)} matched to a client record`, asAt);
}

/** Validates `?as_at=`: null when absent, false when malformed. */
export function parseAsAt(v: string | null): string | null | false {
    if (!v) return null;
    return /^\d{4}-\d{2}-\d{2}$/.test(v) && !isNaN(new Date(`${v}T00:00:00`).getTime()) ? v : false;
}

function asAtDate(asAt: string): Date {
    return new Date(`${asAt}T00:00:00`);
}

const daysFrom = (a: Date, b: Date) => Math.round((b.getTime() - a.getTime()) / DAY);

/* ------------------------------------------------------------------ */
/* Routine tests / health screening                                    */
/* ------------------------------------------------------------------ */

interface TestType {
    key: string;
    label: string;
    interval: number; // months
    currency: number; // chance an eligible client is up to date
    clinic: string;
    eligible: (m: Member) => boolean;
}

const TEST_TYPES: TestType[] = [
    { key: 'health_check_715', label: 'Annual Health Check (715)', interval: 12, currency: 0.94, clinic: 'community_clinic', eligible: () => true },
    { key: 'dental', label: 'Dental', interval: 12, currency: 0.86, clinic: 'visiting_dentist', eligible: () => true },
    { key: 'eyes', label: 'Eyes', interval: 24, currency: 0.9, clinic: 'visiting_optometrist', eligible: () => true },
    { key: 'hearing', label: 'Hearing', interval: 12, currency: 0.75, clinic: 'hearing_outreach', eligible: () => true },
    { key: 'podiatry', label: 'Podiatry', interval: 6, currency: 0.84, clinic: 'community_clinic', eligible: m => m.diabetic },
    { key: 'diabetes_review', label: 'Diabetes Review', interval: 6, currency: 0.88, clinic: 'community_clinic', eligible: m => m.diabetic },
    { key: 'bowel_screening', label: 'Bowel Screening', interval: 24, currency: 0.78, clinic: 'community_clinic', eligible: m => m.age >= 50 && m.age <= 74 },
    { key: 'cervical_screening', label: 'Cervical Screening', interval: 60, currency: 0.8, clinic: 'community_clinic', eligible: m => m.gender === 'Female' && m.age >= 25 && m.age <= 74 },
    { key: 'mammogram', label: 'Mammogram', interval: 24, currency: 0.82, clinic: 'regional_hospital', eligible: m => m.gender === 'Female' && m.age >= 50 && m.age <= 74 },
    { key: 'mental_health_review', label: 'Mental Health Review', interval: 12, currency: 0.92, clinic: 'community_clinic', eligible: () => true },
];

const CLINIC_LABELS: Record<string, string> = {
    community_clinic: 'Community Clinic',
    visiting_dentist: 'Visiting Dentist',
    visiting_optometrist: 'Visiting Optometrist',
    hearing_outreach: 'Hearing Outreach',
    regional_hospital: 'Regional Hospital',
};

interface Cell {
    member: Member;
    test: TestType;
    eligible: boolean;
    last: Date | null;
    booked: Date | null;
    recorded: string | null; // the sheet's own traffic-light status
}

function screeningCells(): Cell[] {
    const t = today();
    const cells: Cell[] = [];
    for (const member of registerCohort()) {
        for (const test of TEST_TYPES) {
            const r = rng(`routine:${member.client.code}:${test.key}`);
            const eligible = test.eligible(member);
            let last: Date | null = null;
            let booked: Date | null = null;
            if (eligible && !r.bool(0.04)) {
                const span = test.interval * 30.4;
                // Up to date: within the interval; otherwise overdue by up to ~5 months.
                last = r.bool(test.currency) ? addDays(t, -r.int(10, Math.round(span) - 12)) : addDays(t, -Math.round(span) - r.int(5, 150));
            }
            const lapsed = !last || addDays(last, Math.round(test.interval * 30.4)) <= t;
            if (eligible && lapsed && r.bool(0.45)) {
                booked = addDays(t, r.int(4, 55));
            }
            cells.push({ member, test, eligible, last, booked, recorded: null });
        }
    }
    return cells;
}

function dueOf(c: Cell): Date | null {
    return c.last ? new Date(c.last.getFullYear(), c.last.getMonth() + c.test.interval, c.last.getDate()) : null;
}

function derivedStatus(c: Cell, at: Date): string {
    if (!c.eligible) return 'Not Applicable';
    if (c.test.key === 'mammogram' && !c.last) return 'Tracked Separately';
    if (!c.last) return 'No Record';
    const due = dueOf(c) as Date;
    if (due > at) return 'On Track';
    return c.booked ? 'Overdue – Booked' : 'Overdue';
}

export function routineTestsStats(asAtParam: string | null = null): Record<string, unknown> {
    const asAt = asAtParam || ymd(today());
    const at = asAtDate(asAt);
    const reg: RegisterKey = 'routine_tests';
    const members = registerCohort();
    const cells = screeningCells();

    const testTypes: Record<string, string> = {};
    TEST_TYPES.forEach(tt => (testTypes[tt.key] = tt.label));

    const current = (c: Cell) => !!c.last && (dueOf(c) as Date) > at;
    const byTest = TEST_TYPES.map(tt => {
        const eligible = cells.filter(c => c.test === tt && c.eligible);
        return {
            test_type_key: tt.key,
            test_type: tt.label,
            ...pct(eligible.filter(current).length, eligible.length, 'register_roster_eligible', fileLabel(reg, `eligible for ${tt.label}`), asAt),
        };
    }).filter(row => (row['denominator'] as number) > 0);

    const allCurrent = members.filter(m => cells.filter(c => c.member.client.code === m.client.code && c.eligible).every(current)).length;

    const overdue = cells
        .filter(c => c.eligible && !current(c) && c.test.key !== 'mammogram')
        .map(c => {
            const due = dueOf(c);
            return {
                ...identity(reg, c.member),
                test_type: c.test.label,
                test_type_key: c.test.key,
                interval_months: c.test.interval,
                last_completed: c.last ? ymd(c.last) : null,
                due: due ? ymd(due) : null,
                days_overdue: due ? Math.max(0, daysFrom(due, at)) : null,
                no_record: !c.last,
            };
        })
        .sort((a, b) => (b.days_overdue ?? 9999) - (a.days_overdue ?? 9999));

    const notApplicable = cells
        .filter(c => !c.eligible && ['mammogram', 'cervical_screening', 'bowel_screening', 'podiatry', 'diabetes_review'].includes(c.test.key))
        .filter(c => rng(`routine:na:${c.member.client.code}:${c.test.key}`).bool(0.35))
        .map(c => ({ ...identity(reg, c.member), test_type: c.test.label, raw_value: 'N/A' }));

    // Appointments: one attended per completed test, plus bookings and a few misses.
    const byStatus: Record<string, number> = { attended: 0, booked: 0, did_not_attend: 0, refused: 0, did_not_attend_hospital: 0, awaiting_appointment: 0, '': 0 };
    const byClinic: Record<string, number> = {};
    const upcoming: Record<string, unknown>[] = [];
    for (const c of cells.filter(x => x.eligible)) {
        const rc = rng(`routine:appt:${c.member.client.code}:${c.test.key}`);
        const attended = c.last ? 1 + rc.int(0, c.test.interval <= 12 ? 1 : 0) : 0;
        byStatus['attended'] += attended;
        byClinic[c.test.clinic] = (byClinic[c.test.clinic] || 0) + attended;
        if (c.booked) {
            byStatus['booked']++;
            byClinic[c.test.clinic]++;
            if (c.booked > at) {
                upcoming.push({ ...identity(reg, c.member), test_type_key: c.test.key, appointment_date: ymd(c.booked), clinic: CLINIC_LABELS[c.test.clinic], status: 'booked' });
            }
        }
        if (!current(c)) {
            const missed = rc.weighted([['', 3], ['did_not_attend', 3], ['awaiting_appointment', 2], ['refused', 1], ['did_not_attend_hospital', 0.4], ['none', 4]] as [string, number][]);
            if (missed !== 'none') {
                byStatus[missed]++;
                const clinic = missed === '' ? '' : c.test.clinic;
                byClinic[clinic] = (byClinic[clinic] || 0) + 1;
            }
        }
    }
    upcoming.sort((a, b) => String(a['appointment_date']).localeCompare(String(b['appointment_date'])));
    for (const k of Object.keys(byStatus)) {
        if (!byStatus[k]) delete byStatus[k];
    }

    const waitPick = rng('routine:waitlist').sample(members.filter(m => !typos(reg).has(m.client.code)), 3);
    const dueForFollowUp = waitPick.map((m, i) => {
        const waitlisted = addDays(at, -(60 + i * 23));
        const followUp = addDays(waitlisted, 90);
        return {
            ...identity(reg, m),
            date_waitlisted: ymd(waitlisted),
            follow_up_due: ymd(followUp),
            days_waiting: daysFrom(waitlisted, at),
            clinic: ['Hearing Outreach', 'Visiting Dentist', 'Regional Hospital'][i],
            appointment_type: ['Audiology assessment', 'Dental check and clean', 'Mammogram'][i],
        };
    });
    const divergences = dueForFollowUp.slice(0, 1).map(w => ({
        client: w.client,
        client_matched: w.client_matched,
        casa: ymd(addDays(asAtDate(w.follow_up_due), -9)),
        derived: w.follow_up_due,
    }));

    // The sheet's own traffic lights mostly agree with the derived status.
    const grid = new Map<string, number>();
    const disagreements: Record<string, unknown>[] = [];
    const disagreeByTest: Record<string, number> = {};
    let gridCells = 0;
    for (const c of cells.filter(x => x.eligible)) {
        gridCells++;
        const derived = derivedStatus(c, at);
        const rc = rng(`routine:grid:${c.member.client.code}:${c.test.key}`);
        let recorded: string | null = derived;
        if (rc.bool(0.03)) {
            recorded = null;
        } else if (rc.bool(c.test.key === 'hearing' ? 0.14 : 0.04)) {
            recorded = derived === 'On Track' ? 'Overdue' : 'On Track';
        }
        c.recorded = recorded;
        const key = `${c.test.label}|${recorded ?? ''}`;
        grid.set(key, (grid.get(key) || 0) + 1);
        if (recorded !== null && recorded !== derived) {
            disagreeByTest[c.test.label] = (disagreeByTest[c.test.label] || 0) + 1;
            const due = dueOf(c);
            disagreements.push({
                ...identity(reg, c.member),
                test_type: c.test.label,
                casa_status: recorded,
                derived_status: derived,
                derived_days_overdue: due && due <= at ? daysFrom(due, at) : null,
                derived_inputs: {
                    last_appointment: c.last ? ymd(c.last) : null,
                    next_booked: c.booked ? ymd(c.booked) : null,
                    interval_months: c.test.interval,
                    interval_source: 'register',
                    as_at: asAt,
                    not_applicable: false,
                },
            });
        }
    }
    const byGrid = [...grid.entries()].map(([k, clients]) => {
        const [test_type, status] = k.split('|');
        return { test_type, casa_status: status === '' ? null : status, clients };
    });

    // Last completed quarter: the sheet's traffic lights vs this dashboard.
    const prevQ = quarterOf(addDays(currentQuarter().start, -1));
    const periodEnd = prevQ.end;
    const casaDenominators: Record<string, number> = {};
    const comparison = TEST_TYPES.filter(tt => tt.key !== 'mammogram').map(tt => {
        const eligible = cells.filter(c => c.test === tt && c.eligible);
        const num = eligible.filter(c => !!c.last && c.last <= periodEnd && (dueOf(c) as Date) > periodEnd).length;
        const rc = rng(`routine:board:${tt.key}`);
        const casaDen = Math.max(1, eligible.length - (rc.bool(0.4) ? 1 : 0));
        const casaNum = Math.min(casaDen, Math.max(0, num + (rc.bool(0.35) ? rc.pick([-1, 1, 2]) : 0)));
        casaDenominators[String(casaDen)] = (casaDenominators[String(casaDen)] || 0) + 1;
        const derived = eligible.length ? round4(num / eligible.length) : null;
        const casa = round4(casaNum / casaDen);
        const agrees = derived === null ? null : Math.abs(casa - derived) < 0.02;
        return {
            test_type: tt.label,
            test_type_key: tt.key,
            period_label: `Q${prevQ.q} FY${String(prevQ.fy).slice(2)}`,
            period_start: ymd(prevQ.start),
            period_end: ymd(prevQ.end),
            period_is_inferred: false,
            casa_compliant: casa,
            casa_not_compliant: round4(1 - casa),
            casa_denominator: casaDen,
            derived_compliant: derived,
            derived_numerator: num,
            derived_denominator: eligible.length,
            variance_explanation: agrees ? null : casaDen !== eligible.length ? 'Sheet leaves out a client who joined late in the quarter' : 'Appointment recorded after the quarter closed',
            agrees,
        };
    });

    const n = members.length;
    const indicator = (name: string, unknown: number, blank: number) => ({ indicator: name, unknown, blank, answered: n - unknown - blank, total: n });

    const sheetRows = members.length * TEST_TYPES.length;
    const needsReview = reviewRows('routine_tests').length;

    return {
        register: reg,
        source: source(reg, sheetRows, needsReview),
        roster: roster(reg, n),
        test_types: testTypes,
        screening_compliance: {
            overall: pct(allCurrent, n, 'register_roster', fileLabel(reg), asAt),
            by_test_type: byTest,
        },
        overdue_screenings: overdue,
        not_applicable: notApplicable,
        appointments: { by_status: byStatus, by_clinic: byClinic, upcoming },
        waitlist: { due_for_follow_up: dueForFollowUp, follow_up_divergences: divergences },
        casa_grid: {
            by_status: byGrid,
            disagreement_summary: { cells: gridCells, disagreements: disagreements.length, by_test_type: disagreeByTest },
            disagreements,
        },
        traffic_lights: { board_comparison: comparison, casa_denominators: casaDenominators },
        risk_indicator_completeness: [
            indicator('sexually_active', 7, 5),
            indicator('alcohol_use', 4, 3),
            indicator('smoker', 1, 2),
            indicator('chronic_kidney_disease', 2, 1),
            indicator('rheumatic_heart_disease', 3, 2),
        ].sort((a, b) => a.answered - b.answered),
        match_quality: matchQuality(reg, members, asAt),
    };
}

/* ------------------------------------------------------------------ */
/* Vaccinations                                                        */
/* ------------------------------------------------------------------ */

type VaxStatus = 'yes' | 'no' | 'not_due' | 'see_comments' | 'unknown';

function vaxStatus(r: Rng, m: Member, vaccine: string, season: number | null): VaxStatus {
    switch (vaccine) {
        case 'flu':
            return r.weighted([['yes', season === 2024 ? 80 : season === 2025 ? 85 : 89], ['no', 9], ['see_comments', 2], ['unknown', season === 2024 ? 6 : 2]] as [VaxStatus, number][]);
        case 'pneumococcal':
            return m.age < 50 ? 'not_due' : r.weighted([['yes', 72], ['no', 18], ['unknown', 10]] as [VaxStatus, number][]);
        case 'covid19':
            return r.weighted([['yes', 84], ['no', 10], ['see_comments', 6]] as [VaxStatus, number][]);
        case 'shingles':
            return m.age < 50 ? 'not_due' : r.weighted([['yes', 55], ['no', 25], ['unknown', 20]] as [VaxStatus, number][]);
        case 'tetanus':
            return r.weighted([['yes', 45], ['unknown', 45], ['no', 10]] as [VaxStatus, number][]);
        default:
            return r.weighted([['yes', 62], ['no', 24], ['unknown', 14]] as [VaxStatus, number][]);
    }
}

export function vaccinationStats(asAtParam: string | null = null): Record<string, unknown> {
    const asAt = asAtParam || ymd(today());
    const at = asAtDate(asAt);
    const reg: RegisterKey = 'vaccination';
    const members = registerCohort();
    const coverage: Record<string, unknown>[] = [];
    const statusOf = new Map<string, VaxStatus>();

    const season = (year: number | null) => (year === null ? null : year);
    const entries: [string, number | null][] = [['flu', 2024], ['flu', 2025], ['flu', 2026], ['pneumococcal', null], ['covid19', null], ['shingles', null], ['tetanus', null], ['hepatitis_b', null]];
    for (const [vaccine, year] of entries) {
        // Earlier flu seasons list fewer clients (some joined the cohort later).
        const listed = members.filter(m => year === null || year === 2026 || new Date(m.client.joined) < new Date(year, 3, 1));
        const breakdown: Partial<Record<VaxStatus, number>> = {};
        for (const m of listed) {
            const st = vaxStatus(rng(`vax:${m.client.code}:${vaccine}:${season(year)}`), m, vaccine, year);
            statusOf.set(`${m.client.code}:${vaccine}:${year}`, st);
            breakdown[st] = (breakdown[st] || 0) + 1;
        }
        const label = vaccine === 'flu' ? `Flu ${year}` : { pneumococcal: 'Pneumococcal', covid19: 'Covid-19', shingles: 'Shingles', tetanus: 'Tetanus', hepatitis_b: 'Hepatitis B' }[vaccine];
        coverage.push({
            vaccine,
            season_year: year,
            coverage: pct(breakdown.yes || 0, listed.length, 'register_roster', fileLabel(reg, label), asAt),
            status_breakdown: breakdown,
        });
    }

    const booster = (vaccine: string) => {
        let current = 0;
        let overdue = 0;
        let none = 0;
        for (const m of members) {
            const r = rng(`vax:booster:${vaccine}:${m.client.code}`);
            const v = r.weighted([['current', 45], ['overdue', 22], ['none', 33]] as [string, number][]);
            if (v === 'current') current++;
            else if (v === 'overdue') overdue++;
            else none++;
        }
        return { vaccine, current, overdue, no_record: none, as_at: asAt };
    };

    const fluRows = members
        .filter(m => statusOf.get(`${m.client.code}:flu:2026`) === 'yes')
        .map(m => {
            const dose = new Date(2026, 3 + rng(`vax:flu:date:${m.client.code}`).int(0, 2), rng(`vax:flu:day:${m.client.code}`).int(1, 28));
            return { ...identity(reg, m), dose_date: ymd(dose), days_since: daysFrom(dose, at) };
        })
        .filter(row => row.days_since >= 0)
        .sort((a, b) => b.days_since - a.days_since);

    const sample = (seed: string, k: number) => rng(seed).sample(members, k);
    const refusals: Record<string, unknown>[] = [];
    for (const m of members) {
        for (const vaccine of ['covid19', 'flu']) {
            const st = statusOf.get(`${m.client.code}:${vaccine}:${vaccine === 'flu' ? 2026 : null}`);
            if (st === 'no' && rng(`vax:refusal:${m.client.code}:${vaccine}`).bool(0.6)) {
                refusals.push({
                    ...identity(reg, m),
                    vaccine,
                    comments: rng(`vax:refusal:c:${m.client.code}`).pick(['Declined – family decision', 'Declined after discussion with clinic nurse', 'Refused on the day – to re-offer', 'Prefers to wait until back on community']),
                });
            }
        }
    }

    const statementHolders = members.filter(m => !rng(`vax:statement:${m.client.code}`).bool(0.13));
    const missingTypo = typos(reg);

    return {
        register: reg,
        source: source(reg, members.length * 8 + 9, reviewRows(reg).length),
        roster: roster(reg, members.length),
        coverage,
        booster_currency: [booster('diphtheria'), booster('tetanus')],
        flu_timeliness: { rows: fluRows, excluded_future_dated: 1, season_year: 2026 },
        paperwork_follow_ups: sample('vax:paperwork', 2).map((m, i) => ({ ...identity(reg, m), cells: i === 0 ? { K: 'FFFFFF00', L: 'FFFFFF00' } : { N: 'FFFFFF00' } })),
        refusal_flags: refusals,
        immunisation_statements: {
            coverage: pct(statementHolders.length, members.length, 'register_roster', fileLabel(reg, 'immunisation history statements'), asAt),
            compared_against_flu_season: 2026,
            missing_from_register: [...missingTypo.values()].map(t => ({ client: null, client_matched: false, raw_name: t.raw, match_status: t.status })),
            stale_or_undated: sample('vax:stale', 3).map((m, i) => ({ ...identity(reg, m), statement_year: i === 2 ? null : 2019 + i * 2, link_kind: (['filename', 'url', 'missing_marker'] as const)[i] })),
        },
        match_quality: matchQuality(reg, members, asAt),
    };
}

/* ------------------------------------------------------------------ */
/* Client plans                                                        */
/* ------------------------------------------------------------------ */

const PLAN_TYPES: { key: string; label: string; applies: number }[] = [
    { key: 'ipp', label: 'IPP', applies: 1 },
    { key: 'meal_management', label: 'Meal Management', applies: 0.45 },
    { key: 'behaviour_support', label: 'Behaviour Support', applies: 0.35 },
];

interface PlanCell {
    member: Member;
    type: string;
    applies: boolean;
    status: string; // review_date_status
    planDate: Date | null;
    reviewDate: Date | null;
    flagged: boolean;
}

function planCells(): PlanCell[] {
    const t = today();
    const out: PlanCell[] = [];
    for (const member of registerCohort()) {
        for (const pt of PLAN_TYPES) {
            const r = rng(`plans:${member.client.code}:${pt.key}`);
            const applies = r.bool(pt.applies);
            if (!applies) {
                out.push({ member, type: pt.key, applies, status: r.bool(0.1) ? 'not_applicable_queried' : 'not_applicable', planDate: null, reviewDate: null, flagged: false });
                continue;
            }
            const noPlan = r.bool(pt.key === 'ipp' ? 0.06 : 0.12);
            const planDate = noPlan ? null : addDays(t, -r.int(30, 430));
            const status = !planDate ? 'in_progress' : r.weighted([['dated', 86], ['in_progress', 6], ['month_only', 4], ['expired', 4]] as [string, number][]);
            const cycle = 12;
            const reviewDate = planDate && (status === 'dated' || status === 'month_only' || status === 'expired') ? new Date(planDate.getFullYear(), planDate.getMonth() + cycle, planDate.getDate() + (r.bool(0.12) ? r.int(70, 120) : 0)) : null;
            out.push({ member, type: pt.key, applies, status, planDate, reviewDate, flagged: r.bool(0.08) });
        }
    }
    return out;
}

export function plansStats(asAtParam: string | null = null): Record<string, unknown> {
    const asAt = asAtParam || ymd(today());
    const at = asAtDate(asAt);
    const reg: RegisterKey = 'plans';
    const members = registerCohort();
    const cells = planCells();

    const derivedDue = (c: PlanCell) => (c.planDate ? new Date(c.planDate.getFullYear() + 1, c.planDate.getMonth(), c.planDate.getDate()) : null);
    const isOverdue = (c: PlanCell) => !!c.reviewDate && c.reviewDate <= at;

    const byPlanType = PLAN_TYPES.map(pt => {
        const rows = cells.filter(c => c.type === pt.key);
        const applicable = rows.filter(c => c.applies);
        const withPlan = applicable.filter(c => c.planDate);
        const dated = withPlan.filter(c => c.reviewDate);
        return {
            plan_type: pt.key,
            plan_coverage: pct(withPlan.length, applicable.length, 'register_roster', fileLabel(reg, pt.label), asAt),
            review_currency: pct(dated.filter(c => !isOverdue(c)).length, dated.length, 'register_roster', fileLabel(reg, `${pt.label}, plans with a review date`), asAt),
        };
    });

    const overdue = cells
        .filter(c => c.applies && (isOverdue(c) || (c.status === 'in_progress' && c.planDate)))
        .map(c => {
            const due = c.reviewDate ?? derivedDue(c);
            const days = c.reviewDate && isOverdue(c) ? daysFrom(c.reviewDate, at) : null;
            return {
                ...identity(reg, c.member),
                plan_type: c.type,
                plan_date: c.planDate ? ymd(c.planDate) : null,
                review_date: c.reviewDate ? ymd(c.reviewDate) : null,
                review_date_status: c.status,
                derived_review_due: due ? ymd(due) : null,
                days_overdue: days,
                flagged: c.flagged,
            };
        })
        .sort((a, b) => (b.days_overdue ?? -1) - (a.days_overdue ?? -1));

    const breakdown: Record<string, Record<string, number>> = {};
    for (const pt of PLAN_TYPES) {
        const m: Record<string, number> = {};
        cells.filter(c => c.type === pt.key).forEach(c => (m[c.status] = (m[c.status] || 0) + 1));
        breakdown[pt.key] = m;
    }

    const divergences = cells
        .filter(c => c.planDate && c.reviewDate)
        .map(c => ({ c, apart: Math.abs(daysFrom(derivedDue(c) as Date, c.reviewDate as Date)) }))
        .filter(x => x.apart > 45)
        .map(({ c, apart }) => ({
            ...identity(reg, c.member),
            plan_type: c.type,
            plan_date: ymd(c.planDate as Date),
            review_date: ymd(c.reviewDate as Date),
            derived_review_due: ymd(derivedDue(c) as Date),
            derived_cycle_months: 12,
            days_apart: apart,
        }));

    const dmy = (d: Date | null) => (d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}` : '');
    const flagged = cells
        .filter(c => c.flagged && c.planDate)
        .map(c => {
            const planFlag = rng(`plans:flag:${c.member.client.code}:${c.type}`).bool(0.35);
            return {
                ...identity(reg, c.member),
                plan_type: c.type,
                plan_date_flagged: planFlag,
                review_date_flagged: !planFlag || !!c.reviewDate,
                plan_date_raw: dmy(c.planDate),
                review_date_raw: dmy(c.reviewDate),
            };
        });

    return {
        register: reg,
        source: source(reg, members.length * 3 + 4, reviewRows(reg).length),
        roster: roster(reg, members.length),
        by_plan_type: byPlanType,
        overdue_reviews: overdue,
        review_status_breakdown: breakdown,
        cycle_divergences: divergences,
        flagged_cells: flagged,
        match_quality: matchQuality(reg, members, asAt),
    };
}

/* ------------------------------------------------------------------ */
/* Restrictive practices                                               */
/* ------------------------------------------------------------------ */

interface Practice {
    sequence_number: number;
    client_initials: string;
    practice_type: string;
    description: string;
    status: string;
    nt_authorisation_status: string;
    expiryOffset: number | null; // days from today
    strategy: 'with_strategy' | 'placeholder' | 'missing';
}

const PRACTICES: Practice[] = [
    { sequence_number: 1, client_initials: 'KN', practice_type: 'chemical_restraint', description: 'Risperidone 0.5mg PRN for severe distress', status: 'authorised', nt_authorisation_status: 'approved', expiryOffset: 19, strategy: 'with_strategy' },
    { sequence_number: 2, client_initials: 'KN', practice_type: 'environmental_restraint', description: 'Kitchen knives kept in a locked drawer', status: 'authorised', nt_authorisation_status: 'approved', expiryOffset: 142, strategy: 'with_strategy' },
    { sequence_number: 3, client_initials: 'TW', practice_type: 'chemical_restraint', description: 'Olanzapine 5mg nightly for behaviour', status: 'active_in_review', nt_authorisation_status: 'submitted_and_awaiting_outcome', expiryOffset: -6, strategy: 'placeholder' },
    { sequence_number: 4, client_initials: 'TW', practice_type: 'physical_restraint', description: 'Two-person escort hold during road travel', status: 'eliminated', nt_authorisation_status: 'na_elimination_achieved', expiryOffset: null, strategy: 'with_strategy' },
    { sequence_number: 5, client_initials: 'MP', practice_type: 'environmental_restraint', description: 'Door alarm on bedroom at night', status: 'authorised', nt_authorisation_status: 'approved', expiryOffset: 52, strategy: 'with_strategy' },
    { sequence_number: 6, client_initials: 'MP', practice_type: 'chemical_restraint', description: 'Diazepam 5mg PRN before clinic visits', status: 'eliminated', nt_authorisation_status: 'na_elimination_achieved', expiryOffset: null, strategy: 'with_strategy' },
    { sequence_number: 7, client_initials: 'RJ', practice_type: 'environmental_restraint', description: 'Locked fridge overnight', status: 'unauthorised', nt_authorisation_status: 'work_in_progress', expiryOffset: null, strategy: 'missing' },
    { sequence_number: 8, client_initials: 'RJ', practice_type: 'chemical_restraint', description: 'Quetiapine 25mg PRN', status: 'authorised', nt_authorisation_status: 'approved', expiryOffset: 81, strategy: 'placeholder' },
    { sequence_number: 9, client_initials: 'DL', practice_type: 'environmental_restraint', description: 'Car keys stored in a locked box', status: 'eliminated', nt_authorisation_status: 'na_elimination_achieved', expiryOffset: null, strategy: 'with_strategy' },
    { sequence_number: 10, client_initials: 'DL', practice_type: 'chemical_restraint', description: 'Haloperidol 1mg PRN', status: 'active_in_review', nt_authorisation_status: 'submitted_and_awaiting_outcome', expiryOffset: 204, strategy: 'placeholder' },
    { sequence_number: 11, client_initials: 'AW', practice_type: 'chemical_restraint', description: 'Sodium valproate for behaviour (not epilepsy)', status: 'authorised', nt_authorisation_status: 'approved', expiryOffset: null, strategy: 'placeholder' },
    { sequence_number: 12, client_initials: 'AW', practice_type: 'mechanical_restraint', description: 'Lap belt on wheelchair during travel', status: '', nt_authorisation_status: '', expiryOffset: 268, strategy: 'placeholder' },
];

function practiceSummary(p: Practice): Record<string, unknown> {
    return {
        sequence_number: p.sequence_number,
        client_initials: p.client_initials,
        practice_type: p.practice_type,
        description: p.description,
        status: p.status,
        nt_authorisation_status: p.nt_authorisation_status,
        authorisation_expiry: p.expiryOffset === null ? null : ymd(addDays(today(), p.expiryOffset)),
    };
}

export function restrictivePracticesStats(): Record<string, unknown> {
    const reg: RegisterKey = 'restrictive_practices';
    const sparse = (key: (p: Practice) => string) => {
        const m: Record<string, number> = {};
        PRACTICES.forEach(p => (m[key(p)] = (m[key(p)] || 0) + 1));
        return m;
    };
    const live = (p: Practice) => p.status !== 'eliminated';
    const expiring = (days: number) => PRACTICES.filter(p => live(p) && p.expiryOffset !== null && p.expiryOffset <= days).sort((a, b) => (a.expiryOffset as number) - (b.expiryOffset as number)).map(practiceSummary);
    return {
        register: reg,
        source: source(reg, PRACTICES.length, reviewRows(reg).length),
        clients_subject: new Set(PRACTICES.filter(live).map(p => p.client_initials)).size,
        by_status: sparse(p => p.status),
        by_type: sparse(p => p.practice_type),
        by_authorisation_status: sparse(p => p.nt_authorisation_status),
        unauthorised_in_use: PRACTICES.filter(p => p.status === 'unauthorised').map(practiceSummary),
        expiring_within_days: { '30': expiring(30), '60': expiring(60), '90': expiring(90) },
        missing_expiry: PRACTICES.filter(p => live(p) && p.expiryOffset === null).map(practiceSummary),
        chemical_restraints: PRACTICES.filter(p => p.practice_type === 'chemical_restraint').map(practiceSummary),
        reduction_strategy: {
            practices: PRACTICES.length,
            with_strategy: PRACTICES.filter(p => p.strategy === 'with_strategy').length,
            placeholder: PRACTICES.filter(p => p.strategy === 'placeholder').length,
            missing: PRACTICES.filter(p => p.strategy === 'missing').length,
        },
        client_identity: { match_status: 'unmatched_by_design', matchable: false },
        needs_review: reviewRows(reg).length,
    };
}

/* ------------------------------------------------------------------ */
/* Review queue                                                        */
/* ------------------------------------------------------------------ */

interface ReviewRow {
    register: RegisterKey;
    table: string;
    source_file: string;
    source_sheet: string;
    source_row: number;
    needs_review: boolean;
    error_codes: string[];
    parse_errors: { code: string; column: string; raw: string }[];
    client_initials?: string;
    client?: string | null;
}

function reviewRows(register: RegisterKey): ReviewRow[] {
    const row = (source_row: number, code: string, column: string, raw: string, extra: Partial<ReviewRow> = {}): ReviewRow => ({
        register,
        table: TABLES[register],
        source_file: FILES[register],
        source_sheet: SHEETS[register],
        source_row,
        needs_review: true,
        error_codes: [code],
        parse_errors: [{ code, column, raw }],
        ...extra,
    });
    const typoRows = [...typos(register).values()].map((t, i) => row(14 + i * 11, t.status === 'ambiguous' ? 'ambiguous_client' : 'unmatched_client', 'A', t.raw, { client: null }));
    switch (register) {
        case 'restrictive_practices':
            return [
                row(9, 'unparseable_date', 'F', '31/13/2026', { client_initials: 'TW' }),
                row(14, 'prose_in_date_column', 'G', 'when BSP renewed', { client_initials: 'AW' }),
                row(15, 'unknown_status_value', 'D', 'on hold?', { client_initials: 'AW' }),
            ];
        case 'routine_tests':
            return [...typoRows, row(37, 'month_only_precision', 'H', 'Mar 2026'), row(52, 'future_date', 'J', ymd(addDays(today(), 210)))];
        case 'vaccination':
            return [...typoRows, row(21, 'sentinel_in_date_column', 'K', 'see file'), row(28, 'mixed_type', 'M', 'Y 2024')];
        case 'plans':
            return [...typoRows, row(11, 'month_only_precision', 'E', 'Feb 2026')];
    }
}

function unresolvedPeople(register: RegisterKey): Record<string, unknown>[] {
    if (register === 'restrictive_practices') {
        return [];
    }
    const cohort = registerCohort();
    return [...typos(register).entries()].map(([code, t]) => {
        const rowsCount = 1 + rng(`review:rows:${register}:${code}`).int(0, 3);
        const candidates =
            t.status === 'ambiguous'
                ? cohort
                      .filter(m => m.client.code.startsWith(t.raw))
                      .slice(0, 3)
                      .map(m => ({ client: m.client.code, name: m.client.code, rule: 'code_prefix', status: 'Active' }))
                : [{ client: code, name: code, rule: 'code_one_character_off', status: 'Active' }];
        if (t.status === 'ambiguous' && candidates.length < 2) {
            const other = activeClients().find(c => c.code.startsWith(t.raw) && c.code !== code);
            candidates.push({ client: code, name: code, rule: 'code_prefix', status: 'Active' });
            if (other) {
                candidates.push({ client: other.code, name: other.code, rule: 'code_prefix', status: 'Active' });
            }
        }
        const unique = candidates.filter((c, i) => candidates.findIndex(x => x.client === c.client) === i);
        return {
            name_key: t.raw.toLowerCase(),
            typed_as: [t.raw],
            match_status: t.status,
            rows: rowsCount,
            registers: { [register]: rowsCount },
            candidates: t.status === 'unmatched' ? [] : unique,
        };
    });
}

function totalsFor(register: RegisterKey): Record<string, number> {
    const t = [...typos(register).values()];
    return {
        rows_needing_review: reviewRows(register).length,
        unmatched: t.filter(x => x.status === 'unmatched').length,
        ambiguous: t.filter(x => x.status === 'ambiguous').length,
        matched_inactive: 0,
        unmatched_by_design: register === 'restrictive_practices' ? PRACTICES.length : 0,
    };
}

/** GET /api/registers/status — the import state of every register. */
export function registerSources(): Record<string, unknown>[] {
    const members = registerCohort().length;
    const rows: Record<RegisterKey, number> = { routine_tests: members * TEST_TYPES.length, vaccination: members * 8 + 9, plans: members * 3 + 4, restrictive_practices: PRACTICES.length };
    return REGISTER_KEYS.map(k => source(k, rows[k], reviewRows(k).length));
}

/** GET /api/registers/review-queue?register=<key>[&limit=]. */
export function registerReviewQueue(register: RegisterKey, limit = 200): Record<string, unknown> {
    const rows = reviewRows(register);
    const people = unresolvedPeople(register);
    const totals = totalsFor(register);
    return {
        totals: { ...totals, unresolved_people: people.length },
        by_register: { [register]: totals },
        unresolved_people: people,
        departed_clients: [],
        rows: rows.slice(0, limit),
        truncated: rows.length > limit,
    };
}
